import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface BoardSeat {
  id: string;
  party_id: string | null;
  label: string;
  kind: string; // defendant | plaintiff | outside | court | other
  seat: string; // top_left | top_right | bottom_left | bottom_right | top | bottom | left | right | center
  pos_x: number | null;
  pos_y: number | null;
  group_id: string | null;
  group_label: string | null;
  ordinal: number;
  note: string | null;
}

/** Per-case board seating. Auto-seeds sensible defaults if the case has none. */
export function useCaseBoardSeats(caseId: string | undefined) {
  return useQuery<BoardSeat[], Error>({
    queryKey: ["board-seats", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("ensure_default_board_seats", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as BoardSeat[]) ?? [];
    },
  });
}

/** Add or move/re-seat a token. Pass the full seat object (with id to update). */
export function useUpsertSeat(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, Partial<BoardSeat>>({
    mutationFn: async (seat) => {
      const { data, error } = await berserkr().rpc("upsert_case_board_seat", {
        p_case_id: caseId,
        p_seat: seat,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-seats", caseId] }),
  });
}

export function useDeleteSeat(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (seatId) => {
      const { error } = await berserkr().rpc("delete_case_board_seat", {
        p_seat_id: seatId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-seats", caseId] }),
  });
}
